import { useCallback, useEffect, useState } from 'react'
import {
  CLOUD_ADMIN_SESSION_EVENT,
  getCloudAdminToken
} from '@core/auth/cloudSession'
import { httpErrorText } from '@core/utils/httpError'
import { cx, hint, table, tableWrap, td, th } from '@ui/classes'
import { eqBtn, eqSmallBtn, errBanner, okBanner, seg, segActive, segBar, segIdle } from '@ui/admin'
import { AdminCard } from './AdminCard'
import { Alert, Check, Dot } from '@ui/icons'
import { CLOUD_API, CLOUD_NOT_CONNECTED } from './shared'
import type { AdminSectionProps } from './types'

interface AlertRow {
  id: number
  kind: string
  severity: 'info' | 'warning' | 'critical'
  status: 'open' | 'acknowledged' | 'resolved'
  device_id: string | null
  source: string | null
  message: string
  details: unknown
  occurrences: number
  first_seen_at: string
  last_seen_at: string
  acknowledged_at: string | null
  resolved_at: string | null
}

interface AuditRow {
  id: number
  actor_role: string | null
  action: string
  target_type: string | null
  target_id: string | null
  result: 'ok' | 'failed' | 'denied'
  error: string | null
  metadata: unknown
  created_at: string
}

const ALERT_KIND_LABELS: Record<string, string> = {
  offline_pending: 'Чеки без ККТ (офлайн)',
  marking_review: 'Чеки на проверке маркировки',
  cz_auth_error: 'Ошибка доступа к Честному Знаку',
  fiscalization_queue_error: 'Очередь пробития на ККТ'
}

const SEVERITY_LABELS: Record<string, string> = {
  info: 'Инфо',
  warning: 'Внимание',
  critical: 'Критично'
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  remote_command_create: 'Создание remote-команды',
  remote_command_result: 'Результат remote-команды',
  remote_device_revoke: 'Отзыв remote-доступа',
  remote_device_enable: 'Возврат remote-доступа'
}

function severityTone(severity: AlertRow['severity']): string {
  if (severity === 'critical') return 'text-dangerDark'
  if (severity === 'warning') return 'text-amber-700'
  return 'text-info'
}

function severityIcon(severity: AlertRow['severity']) {
  if (severity === 'critical') return <Alert size={16} className="text-danger" />
  if (severity === 'warning') return <Alert size={16} className="text-amber-700" />
  return <Dot size={16} className="text-info" />
}

function resultTone(result: AuditRow['result']): string {
  if (result === 'ok') return 'text-okDark'
  if (result === 'denied') return 'text-amber-700'
  return 'text-dangerDark'
}

/**
 * Раздел «Монитор»: операционные алерты и audit-журнал.
 *
 * Данные читаются из cloud-admin API; без входа в облако раздел показывает
 * понятное состояние вместо 401.
 */
export function MonitoringSection(_props: AdminSectionProps) {
  const [token, setToken] = useState(() => getCloudAdminToken())
  const [tab, setTab] = useState<'alerts' | 'audit'>('alerts')
  const [alerts, setAlerts] = useState<AlertRow[]>([])
  const [audit, setAudit] = useState<AuditRow[]>([])
  const [auditAction, setAuditAction] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const sync = () => setToken(getCloudAdminToken())
    sync()
    window.addEventListener(CLOUD_ADMIN_SESSION_EVENT, sync)
    return () => window.removeEventListener(CLOUD_ADMIN_SESSION_EVENT, sync)
  }, [])

  const reloadAlerts = useCallback(async () => {
    if (!token) {
      setAlerts([])
      return
    }
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const resp = await fetch(`${CLOUD_API}/remote/alerts?status=open&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!resp.ok) {
        setError(httpErrorText('Загрузка алертов', resp.status))
        return
      }
      const body = (await resp.json()) as { alerts?: AlertRow[] }
      setAlerts(Array.isArray(body.alerts) ? body.alerts : [])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }, [token])

  const reloadAudit = useCallback(async () => {
    if (!token) {
      setAudit([])
      return
    }
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const params = new URLSearchParams({ limit: '100' })
      if (auditAction.trim()) params.set('action', auditAction.trim())
      const resp = await fetch(`${CLOUD_API}/remote/audit?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!resp.ok) {
        setError(httpErrorText('Загрузка аудита', resp.status))
        return
      }
      const body = (await resp.json()) as { events?: AuditRow[] }
      setAudit(Array.isArray(body.events) ? body.events : [])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }, [token, auditAction])

  useEffect(() => {
    if (tab === 'alerts') void reloadAlerts()
  }, [tab, reloadAlerts])

  useEffect(() => {
    if (tab === 'audit') void reloadAudit()
  }, [tab, reloadAudit])

  const ackAlert = async (id: number) => {
    setBusy(true)
    setError(null)
    try {
      const resp = await fetch(`${CLOUD_API}/remote/alerts/${id}/ack`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!resp.ok) {
        setError(httpErrorText('Подтверждение алерта', resp.status))
        return
      }
      setMessage('Алерт подтверждён')
      await reloadAlerts()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const resolveAlert = async (id: number) => {
    setBusy(true)
    setError(null)
    try {
      const resp = await fetch(`${CLOUD_API}/remote/alerts/${id}/resolve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!resp.ok) {
        setError(httpErrorText('Закрытие алерта', resp.status))
        return
      }
      setMessage('Алерт закрыт')
      await reloadAlerts()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AdminCard
      id="monitor.main"
      title="Монитор"
      defaultOpen
      meta={
        <span className="text-small font-normal text-muted">
          алертов: {alerts.length}
        </span>
      }
      actions={
        <button
          type="button"
          className={eqBtn}
          disabled={busy || !token}
          onClick={() => (tab === 'alerts' ? void reloadAlerts() : void reloadAudit())}
        >
          Обновить
        </button>
      }
    >

      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}
      {!token && (
        <p className={hint}>{CLOUD_NOT_CONNECTED}. Монитор доступен после входа в облако.</p>
      )}

      <div className={segBar}>
        <button
          type="button"
          className={cx(seg, tab === 'alerts' ? segActive : segIdle)}
          onClick={() => setTab('alerts')}
        >
          Алерты ({alerts.length})
        </button>
        <button
          type="button"
          className={cx(seg, tab === 'audit' ? segActive : segIdle)}
          onClick={() => setTab('audit')}
        >
          Аудит
        </button>
      </div>

      {tab === 'alerts' && (
        <>
          {alerts.length === 0 ? (
            <p className={hint}>Открытых алертов нет.</p>
          ) : (
            <div className={tableWrap}>
              <table className={table}>
                <thead>
                  <tr>
                    <th className={th}>Важность</th>
                    <th className={th}>Событие</th>
                    <th className={th}>Сообщение</th>
                    <th className={th}>Касса</th>
                    <th className={th}>Повторов</th>
                    <th className={th}>Последний раз</th>
                    <th className={th}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => (
                    <tr key={alert.id}>
                      <td className={td}>
                        <span className={cx('inline-flex items-center gap-1', severityTone(alert.severity))}>
                          {severityIcon(alert.severity)}
                          {SEVERITY_LABELS[alert.severity] ?? alert.severity}
                        </span>
                      </td>
                      <td className={td}>{ALERT_KIND_LABELS[alert.kind] ?? alert.kind}</td>
                      <td className={td}>{alert.message}</td>
                      <td className={td}>{alert.device_id ?? '—'}</td>
                      <td className={td}>{alert.occurrences}</td>
                      <td className={td}>{new Date(alert.last_seen_at).toLocaleString('ru-RU')}</td>
                      <td className={td}>
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            className={eqSmallBtn}
                            disabled={busy}
                            onClick={() => void ackAlert(alert.id)}
                          >
                            Подтвердить
                          </button>
                          <button
                            type="button"
                            className={eqSmallBtn}
                            disabled={busy}
                            onClick={() => void resolveAlert(alert.id)}
                          >
                            Закрыть
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'audit' && (
        <>
          <div className="mb-2.5 flex flex-wrap items-center gap-2">
            <input
              className="rounded-card border border-lineStrong px-2.5 py-2 text-sm text-ink"
              placeholder="Фильтр по action"
              value={auditAction}
              onChange={(e) => setAuditAction(e.target.value)}
            />
            <button type="button" className={eqBtn} disabled={busy} onClick={() => void reloadAudit()}>
              Применить
            </button>
          </div>
          {audit.length === 0 ? (
            <p className={hint}>Событий аудита нет.</p>
          ) : (
            <div className={tableWrap}>
              <table className={table}>
                <thead>
                  <tr>
                    <th className={th}>Время</th>
                    <th className={th}>Действие</th>
                    <th className={th}>Роль</th>
                    <th className={th}>Объект</th>
                    <th className={th}>Результат</th>
                    <th className={th}>Ошибка</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((event) => (
                    <tr key={event.id}>
                      <td className={td}>{new Date(event.created_at).toLocaleString('ru-RU')}</td>
                      <td className={td}>{AUDIT_ACTION_LABELS[event.action] ?? event.action}</td>
                      <td className={td}>{event.actor_role ?? '—'}</td>
                      <td className={td}>
                        {event.target_type ?? '—'}
                        {event.target_id ? `: ${event.target_id}` : ''}
                      </td>
                      <td className={td}>
                        <span className={cx('inline-flex items-center gap-1', resultTone(event.result))}>
                          {event.result === 'ok' ? <Check size={16} /> : <Alert size={16} />}
                          {event.result}
                        </span>
                      </td>
                      <td className={td}>{event.error ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminCard>
  )
}
